// Synthesized retro SFX via the Web Audio API — no audio files. Each effect is
// described by a data spec (tone = oscillator sweep, noise = filtered white
// noise), so the sound lab can tune the same engine and the tuned values get
// baked back into DEFAULT_AUDIO. All sound is shell-side (the core stays pure).

export interface ToneSpec {
  readonly kind: 'tone';
  readonly wave: OscillatorType;
  readonly from: number; // start frequency (Hz)
  readonly to: number; // end frequency (Hz)
  readonly dur: number; // seconds
  readonly gain: number; // 0..1 peak
}

export interface NoiseSpec {
  readonly kind: 'noise';
  readonly dur: number;
  readonly gain: number;
  readonly cutoff: number; // lowpass cutoff (Hz)
}

export type SfxSpec = ToneSpec | NoiseSpec;

export interface AudioConfig {
  readonly launch: SfxSpec;
  readonly blast: SfxSpec;
  readonly cityLost: SfxSpec;
  readonly gameOver: SfxSpec;
}

/** Lab-tuned defaults (from /sound-lab.html). Re-tune freely there.
 *  The earlier "fire SFX plays at the explosion" report was a timing issue
 *  (cold-start audio latency), fixed by warming the AudioContext on first
 *  gesture — NOT an audibility issue; these tuned values are intentional. */
export const DEFAULT_AUDIO: AudioConfig = {
  launch: { kind: 'tone', wave: 'square', from: 529, to: 20, dur: 0.29, gain: 0.06 },
  blast: { kind: 'noise', dur: 0.99, gain: 0.29, cutoff: 550 },
  cityLost: { kind: 'noise', dur: 1.49, gain: 0.38, cutoff: 400 },
  gameOver: { kind: 'tone', wave: 'sawtooth', from: 38, to: 20, dur: 0.78, gain: 0.11 },
};

function playTone(c: AudioContext, s: ToneSpec): void {
  const t = c.currentTime;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = s.wave;
  osc.frequency.setValueAtTime(s.from, t);
  osc.frequency.exponentialRampToValueAtTime(Math.max(1, s.to), t + s.dur);
  g.gain.setValueAtTime(s.gain, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + s.dur);
  osc.connect(g).connect(c.destination);
  osc.start(t);
  osc.stop(t + s.dur);
}

function playNoise(c: AudioContext, s: NoiseSpec): void {
  const t = c.currentTime;
  const samples = Math.floor(c.sampleRate * s.dur);
  const buffer = c.createBuffer(1, samples, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < samples; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buffer;
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = s.cutoff;
  const g = c.createGain();
  g.gain.setValueAtTime(s.gain, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + s.dur);
  src.connect(filter).connect(g).connect(c.destination);
  src.start(t);
  src.stop(t + s.dur);
}

/** Play a single spec on a given context. Shared by the game and the sound lab. */
export function playSpec(c: AudioContext, spec: SfxSpec): void {
  if (spec.kind === 'tone') playTone(c, spec);
  else playNoise(c, spec);
}

export interface AudioController {
  resume: () => void; // call from a user gesture (autoplay policy)
  setMuted: (muted: boolean) => void;
  toggleMuted: () => boolean;
  launch: () => void;
  blast: () => void;
  cityLost: () => void;
  gameOver: () => void;
}

export function createAudio(config: AudioConfig = DEFAULT_AUDIO): AudioController {
  let ctx: AudioContext | null = null;
  let muted = false;
  let warmed = false;

  const audioCtx = (): AudioContext | null => {
    if (muted) return null;
    if (ctx === null) {
      try {
        ctx = new AudioContext();
      } catch {
        return null;
      }
    }
    return ctx;
  };

  const play = (spec: SfxSpec): void => {
    const c = audioCtx();
    if (c) playSpec(c, spec);
  };

  return {
    resume: (): void => {
      const c = audioCtx();
      if (!c) return;
      if (c.state === 'suspended') void c.resume();
      if (!warmed) {
        // Warm the output graph with an inaudible blip so the first real SFX
        // doesn't eat the cold-start latency (which makes early sounds feel late).
        const g = c.createGain();
        g.gain.value = 0.0001;
        const o = c.createOscillator();
        o.connect(g).connect(c.destination);
        const t = c.currentTime;
        o.start(t);
        o.stop(t + 0.05);
        warmed = true;
      }
    },
    setMuted: (m: boolean): void => {
      muted = m;
    },
    toggleMuted: (): boolean => {
      muted = !muted;
      return muted;
    },
    launch: (): void => play(config.launch),
    blast: (): void => play(config.blast),
    cityLost: (): void => play(config.cityLost),
    gameOver: (): void => play(config.gameOver),
  };
}
